import { useCallback, useEffect, useRef } from 'react';
import { StartOverlay } from '@/components/deai-startoverlay';
import { DEAI3DScene } from '@/components/deai-3dscene';
import { DEAIChatBox, AIMessageDisplay, UserMessageDisplay } from '@/components/deai-chatbox';
import { DEAIApp } from '@/components/deai-app';
import { useAppStore } from '@/hooks/use-app-store';
import { PERSONAS } from '@/types/persona';
import { FortuneDisplayContainer } from '@/components/tgs2025/fortunetelling/FortuneDisplay';
import { useFortuneDisplay } from '@/hooks/use-fortune-display';
import { FortuneTestButtons } from '@/components/tgs2025/fortunetelling/FortuneTestButton';
import { useFortuneContextStore } from '@/hooks/tgs2025/fortunetelling/use-fortune-context-provider';
import { FortuneAIMessageDisplay } from '@/components/tgs2025/fortunetelling/FortuneAIMessageDisplay';


//tgs2025 fortune teller



export const FortuneTellingPage = () => {
    const { triggerAvatarAnimator, start, sendContextUpdate } = useAppStore();
    const { locale, setLocale, getContextData } = useFortuneContextStore();
    const audioRef = useRef<HTMLAudioElement>(null);

    // Initialize fortune display event handling
    useFortuneDisplay();

    // Initialize video trigger handling
    useEffect(()=>{
        triggerAvatarAnimator(
            0,
            'StartIdle'
        );
    },[triggerAvatarAnimator])

    // Start background music after user interaction
    const onStart = () => {
        if (audioRef.current) {
            audioRef.current.volume = 0.05; // 5% volume
            audioRef.current.play().catch(error => {
                console.log('Background music autoplay prevented:', error);
            });
        }
        start();
    };

    // Handle locale change
    const handleLocaleChange = useCallback((newLocale: 'EN' | 'JA') => {
        setLocale(newLocale);
        // Send context update immediately since Zustand will have the latest state
        setTimeout(() => {
            sendContextUpdate();
        }, 50);
    }, [setLocale, sendContextUpdate]);

    // Cleanup background music on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
    }, []);

  return (
    <div className="h-screen">
        {/* Background Music */}
        <audio
          ref={audioRef}
          loop
          preload="auto"
          autoPlay={false}
        >
          <source src="/audio/fortune-bgm.m4a" type="audio/mp4" />
          Your browser does not support the audio element.
        </audio>

        <div
          className="absolute inset-0 bg-cover bg-fixed z-0"
          style={{
            backgroundImage: 'url(/img/SHRINE_BG.png)',
            backgroundPosition: '60% center'
          }}
        />
        <StartOverlay soundURL='audio/click.wav' onStart={onStart} />

        {/* Fortune AI Message Display at top */}
        <div className="absolute top-[2dvh] left-1/2 transform -translate-x-1/2 z-10 w-full">
          <FortuneAIMessageDisplay />
        </div>

        <div className="w-full h-full">
            <DEAI3DScene 
              className="absolute inset-0 h-full w-full"
              placeholderImgSrc="img/loading-placeholder.png"
              vrmUrl={"avatar/Miko.vrm"}
              vrmaUrl={"animations/Noura/Idle/QATAR Idle_clean_02_upd.vrma"}
              vrmaLibRoot={"animations/Noura/"}
              vrmaLibConfig={"AnimationConfig.json"}
              animatorLibRoot={"animators/Noura/"}
              animatorLibConfig={"AnimatorConfig.json"}
              cameraPosition={{x: -0.2, y: 1.35, z: -2.6}}
            />
            <div className="absolute w-3/4 max-w-xl left-16 transform mt-4 bottom-[10dvh] rounded-xl bg-slate-950/60 backdrop-blur-sm p-6 z-20">
              <DEAIChatBox className="flex flex-col text-white rounded-lg" />
              <div className="items-center justify-center flex gap-2 pt-6">
                <button
                  onClick={() => handleLocaleChange('EN')}
                  className={`w-25 py-4 rounded-full font-semibold text-xl transition-colors shadow-md ${
                    locale === 'EN'
                      ? 'bg-sky-600 text-white'
                      : 'bg-white/50 text-red-800'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => handleLocaleChange('JA')}
                  className={`w-25 py-4 rounded-full font-semibold text-xl transition-colors shadow-md ${
                    locale === 'JA'
                      ? 'bg-sky-600 text-white'
                      : 'bg-white/50 text-red-800'
                  }`}
                >
                  日本語
                </button>
              </div>
            </div>
        </div>
        
      <div className="absolute top-5 right-5">
          <p className="text-right text-xs md:text-sm">v{import.meta.env.VITE_APP_VERSION}</p>
      </div>
      
      {/* <AIMessageDisplay />
      <UserMessageDisplay /> */}
      
      <DEAIApp contextProvider={getContextData} persona={PERSONAS.FORTUNE_TELLER}/>
      <FortuneDisplayContainer />
      {/* <FortuneTestButtons /> */}
    </div>
  );
};